import { auth } from './firebase'
import {
  cacheRead, flushOutbox, isNetworkFailure, isOnline, onConnectivityChange,
  queueWrite, readCached, type OutboxEntry,
} from './offline'

/* In dev, Vite proxies /api to the Flask server so the browser sees one origin
 * and the __session cookie behaves normally. In production React is served from
 * the same host as the backend, so a relative base works there too. */
const BASE = import.meta.env.VITE_API_BASE ?? '/api/v1'

export class ApiError extends Error {
  // Declared explicitly rather than as constructor parameter properties -
  // this project builds with erasableSyntaxOnly, which disallows that shorthand.
  status: number
  code: string

  constructor(status: number, code: string, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
  }
}

type Options = Omit<RequestInit, 'body'> & {
  body?: unknown
  /** Queue this write instead of failing when the device is offline, and
   * replay it once the network returns. Set by the `offline` helpers below;
   * deliberately opt-in, because replaying is only safe for captures that
   * create a record, never for sign-in, approvals, or anything whose meaning
   * depends on when it happened. */
  queueWhenOffline?: boolean
  /** Shown in the pending list while the write waits for a network. */
  label?: string
}

export class OfflineQueuedError extends Error {
  entry: OutboxEntry
  constructor(entry: OutboxEntry) {
    super('Saved on this device. It will be sent when you are back online.')
    this.name = 'OfflineQueuedError'
    this.entry = entry
  }
}

/** Marks a value that came from the on-device cache rather than the network,
 * so a screen can say so. Non-enumerable to keep it out of JSON and renders. */
export const FROM_CACHE = Symbol('fromCache')

export function cacheAgeOf(value: unknown): number | null {
  if (!value || typeof value !== 'object') return null
  const age = (value as Record<symbol, unknown>)[FROM_CACHE]
  return typeof age === 'number' ? age : null
}

/* One retry path matters here: the Flask session cookie can expire while the
 * Firebase session is still valid. Rather than bouncing the user to /login, we
 * silently re-exchange a fresh ID token once and replay the request. */
let reexchanging: Promise<boolean> | null = null

async function reexchangeSession(): Promise<boolean> {
  if (!auth.currentUser) return false
  reexchanging ??= (async () => {
    try {
      const idToken = await auth.currentUser!.getIdToken(true)
      const res = await fetch(`${BASE}/auth/exchange`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idToken }),
      })
      return res.ok
    } catch {
      return false
    } finally {
      // Allow a later 401 to trigger a fresh attempt.
      setTimeout(() => { reexchanging = null }, 0)
    }
  })()
  return reexchanging
}

/* Two auth families coexist on the backend: /miner, /leader, /gov, /devices
 * check the __session cookie only; the older generic CRUD surface (/users,
 * /organizations, /sites, /applications, /licenses, /parcels, /cooperatives,
 * /me/allocation-requests, /admin/*, /role-catalog) checks a Bearer token
 * only, with no cookie fallback. Rather than have every caller know which
 * family a given path belongs to, send both on every request - the Firebase
 * SDK caches the ID token and only round-trips when it's actually near
 * expiry, so this doesn't add a real network cost. */
async function request<T>(path: string, options: Options = {}, retry = true,
                          idempotencyKey?: string): Promise<T> {
  const { body, headers, queueWhenOffline, label, ...rest } = options
  const isForm = body instanceof FormData
  const method = (rest.method ?? 'GET').toUpperCase()
  const isRead = method === 'GET'

  // Queue before even trying, when the browser already knows it is offline -
  // no point burning a timeout the user has to sit through.
  if (!isRead && queueWhenOffline && !isForm && !isOnline()) {
    throw new OfflineQueuedError(await queueWrite({
      path, method: method as OutboxEntry['method'], body, label,
    }))
  }

  const idToken = await auth.currentUser?.getIdToken().catch(() => undefined)

  let res: Response
  try {
    res = await fetch(`${BASE}${path}`, {
      ...rest,
      credentials: 'include',
      headers: {
        // FormData sets its own multipart Content-Type (with boundary) - the
        // browser only does this correctly when we don't set one ourselves.
        ...(body !== undefined && !isForm ? { 'Content-Type': 'application/json' } : {}),
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
        // Lets the server recognise a replayed write as one it already
        // applied, so a flaky connection cannot double-post a record.
        ...(idempotencyKey ? { 'X-Idempotency-Key': idempotencyKey } : {}),
        ...headers,
      },
      ...(body !== undefined ? { body: isForm ? (body as FormData) : JSON.stringify(body) } : {}),
    })
  } catch (err) {
    // fetch only rejects on a transport failure - the request never reached
    // a server. Reads fall back to the on-device copy; opted-in writes queue.
    if (isNetworkFailure(err)) {
      if (isRead) {
        const hit = await readCached(path)
        if (hit) {
          const data = hit.data as T
          if (data && typeof data === 'object') {
            Object.defineProperty(data, FROM_CACHE, {
              value: Date.now() - hit.savedAt, enumerable: false, configurable: true,
            })
          }
          return data
        }
      } else if (queueWhenOffline && !isForm) {
        throw new OfflineQueuedError(await queueWrite({
          path, method: method as OutboxEntry['method'], body, label,
        }))
      }
    }
    throw err
  }

  if (res.status === 401 && retry && (await reexchangeSession())) {
    return request<T>(path, options, false, idempotencyKey)
  }

  const text = await res.text()
  const data = text ? safeParse(text) : null

  if (!res.ok) {
    const body = data as { error?: string; message?: string; detail?: string } | null
    throw new ApiError(
      res.status,
      body?.error ?? 'request_failed',
      // Some routes (e.g. allocation requests) send their human-readable
      // explanation under `detail` alongside a generic `error` code, rather
      // than under `message` - without this, the UI showed only the opaque
      // code (e.g. "invalid_request") and silently dropped the real reason.
      body?.message ?? body?.detail ?? body?.error ?? `Request failed (${res.status})`,
    )
  }

  // Keep the last good copy of every read so the screen still opens later on
  // a dead connection. Failures here are ignored on purpose - a full or
  // disabled IndexedDB must never break a working request.
  if (isRead && data && typeof data === 'object') {
    void cacheRead(path, data).catch(() => {})
  }
  return data as T
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return { raw: text }
  }
}

export const api = {
  get:      <T>(p: string) => request<T>(p),
  post:     <T>(p: string, body?: unknown) => request<T>(p, { method: 'POST', body }),
  put:      <T>(p: string, body?: unknown) => request<T>(p, { method: 'PUT', body }),
  patch:    <T>(p: string, body?: unknown) => request<T>(p, { method: 'PATCH', body }),
  del:      <T>(p: string) => request<T>(p, { method: 'DELETE' }),
  // Multipart form POST - for routes that take request.form/request.files
  // server-side rather than a JSON body (e.g. HR contract/medical uploads,
  // which stay non-public and are never routed through client-side Storage).
  postForm: <T>(p: string, body: FormData) => request<T>(p, { method: 'POST', body }),

  /* Offline-tolerant writes. Use these for CAPTURE - recording something
   * that happened in the field - where holding the entry on the phone and
   * sending it later is better than losing it. Do NOT use them for anything
   * whose outcome the person needs to see now (approvals, verification,
   * sign-in) or whose validity depends on the moment it ran.
   *
   * When the write is queued these RESOLVE rather than reject, so the form
   * closes and the list refreshes exactly as it would online - the pending
   * state is reported once, by OfflineBar, instead of as a red error on a
   * capture the person did nothing wrong with. The resolved value carries
   * only `{ queued: true }`: a caller that needs a server-assigned id back
   * must use the plain `api.post` instead. */
  offline: {
    post:  <T>(p: string, body?: unknown, label?: string) =>
      queueable<T>(p, 'POST', body, label),
    put:   <T>(p: string, body?: unknown, label?: string) =>
      queueable<T>(p, 'PUT', body, label),
    patch: <T>(p: string, body?: unknown, label?: string) =>
      queueable<T>(p, 'PATCH', body, label),
  },
}

/** Shared body of api.offline.*: run the request, and turn "queued for later"
 * into a resolved result rather than a thrown error. */
async function queueable<T>(path: string, method: string,
                            body?: unknown, label?: string): Promise<T> {
  try {
    return await request<T>(path, { method, body, queueWhenOffline: true, label })
  } catch (err) {
    if (err instanceof OfflineQueuedError) return { queued: true } as T
    throw err
  }
}

/**
 * Download a file the API generates (Excel/PDF exports, reports).
 *
 * These endpoints authenticate with the Flask __session cookie, so a plain
 * `<a href target="_blank">` USED to work - but only while the app ran in a
 * browser tab. Now that SSM Zambia runs as an installed PWA, target="_blank"
 * hands the URL to the system browser, which has its own cookie jar and no
 * session: the person gets a 401 JSON page instead of their spreadsheet
 * (verified against the live exports).
 *
 * So fetch it inside the app, where the session and the Bearer token both
 * exist, and hand the bytes to the browser as a Blob. The filename comes
 * from Content-Disposition when the server sends one.
 */
export async function downloadFile(path: string, fallbackName: string): Promise<void> {
  const url = path.startsWith('/api/') ? path : `${BASE}${path}`
  const idToken = await auth.currentUser?.getIdToken().catch(() => undefined)

  const res = await fetch(url, {
    credentials: 'include',
    headers: { ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}) },
  })

  if (!res.ok) {
    // Export routes answer with JSON on failure, so surface the real reason
    // rather than saving an error page as if it were a document.
    let message = `Could not download that file (${res.status})`
    try {
      const body = await res.json() as { error?: string; message?: string }
      message = body?.message ?? body?.error ?? message
    } catch { /* not JSON - keep the generic message */ }
    throw new ApiError(res.status, 'download_failed', message)
  }

  const blob = await res.blob()
  const disposition = res.headers.get('content-disposition') ?? ''
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition)
  const name = match ? decodeURIComponent(match[1].trim()) : fallbackName

  const objectUrl = URL.createObjectURL(blob)
  try {
    const a = document.createElement('a')
    a.href = objectUrl
    a.download = name
    document.body.appendChild(a)
    a.click()
    a.remove()
  } finally {
    // Give the browser a moment to start the save before the URL is freed.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000)
  }
}

/** Replay queued writes. Each carries its outbox id as the idempotency key,
 * so a write the server already accepted before the connection dropped is
 * recognised rather than duplicated. */
export function syncOutbox(): Promise<{ sent: number; failed: number }> {
  return flushOutbox((entry) =>
    request(entry.path, { method: entry.method, body: entry.body }, true, entry.id)
      .then(() => undefined))
}

/* Drain automatically: once when the module loads (the app may start with a
 * backlog from a previous session) and again whenever the network returns. */
if (typeof window !== 'undefined') {
  void syncOutbox().catch(() => {})
  onConnectivityChange((online) => {
    if (online) void syncOutbox().catch(() => {})
  })
}
